/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AgencyInput } from '../housien-models/AgencyInput';
import type { AgencyModel } from '../housien-models/AgencyModel';
import type { AgencyModelReturnFindById } from '../housien-models/AgencyModelReturnFindById';
import type { SuccessResponse } from '../housien-models/SuccessResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';

/**
 * Creates a new agency via `POST /agencies`.
 *
 * @param requestBody - The agency creation payload
 * @returns A cancelable promise resolving to the create success response
 */
export function createAgency(
requestBody: AgencyInput,
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'POST',
        url: '/agencies',
        body: requestBody,
        mediaType: 'application/json',
    });
}

/**
 * Lists agencies via `GET /agencies`, fetching the first page (up to 100 items).
 *
 * @returns A cancelable promise resolving to the array of agencies
 */
export function getAllAgencies(
): CancelablePromise<Array<AgencyModel>> {
    return __request(OpenAPIHousien, {
        method: 'GET',
        url: '/agencies',
        query: {
            '_page': 1,
            '_limit': 100,
        },
    });
}

/**
 * Fetches a single agency by id via `GET /agencies/{agencyId}`.
 *
 * @param agencyId - The identifier of the agency to retrieve
 * @returns A cancelable promise resolving to the agency detail
 */
export function getAgency(
agencyId: string,
): CancelablePromise<AgencyModelReturnFindById> {
    return __request(OpenAPIHousien, {
        method: 'GET',
        url: '/agencies/{agencyId}',
        path: {
            'agencyId': agencyId,
        },
    });
}

/**
 * Updates an agency's profile fields via `PUT /agencies/{agencyId}`.
 *
 * @param agencyId - The agency identifier
 * @param requestBody - The partial set of agency fields to update
 * @returns A cancelable promise resolving to the updated agency detail model
 */
/**
 * Updates an agency's profile fields via `PUT /agencies/{agencyId}`.
 *
 * @param agencyId - The identifier of the agency to update
 * @param requestBody - The partial set of agency fields to change
 * @returns A cancelable promise resolving to the updated agency detail
 */
export function updateAgency(
agencyId: string,
requestBody: {
name?: string;
cnpj?: string;
cpf?: string;
postal_code?: string;
city?: string;
state?: string;
number?: string;
complement?: string;
neighborhood?: string;
description?: string;
street?: string;
phone?: string;
email?: string;
site?: string;
creci?: string;
},
): CancelablePromise<AgencyModelReturnFindById> {
    return __request(OpenAPIHousien, {
        method: 'PUT',
        url: '/agencies/{agencyId}',
        path: {
            'agencyId': agencyId,
        },
        body: requestBody,
        mediaType: 'application/json',
    });
}

/**
 * Abandons an agency whose checkout was never finished, via
 * `DELETE /agencies/{agencyId}`.
 *
 * Only the agency's creator may abandon it, and only while it holds no
 * subscription — refused with 403/409 otherwise.
 *
 * @param agencyId - The identifier of the agency to abandon
 * @returns A cancelable promise resolving to the success response
 */
export function abandonAgency(
agencyId: string,
): CancelablePromise<SuccessResponse> {
    return __request(OpenAPIHousien, {
        method: 'DELETE',
        url: '/agencies/{agencyId}',
        path: {
            'agencyId': agencyId,
        },
    });
}

/**
 * Reconciles an agency's subscription state with the payment provider via
 * `POST /agencies/{agencyId}/subscription/reconcile`.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the reconciled agency detail model
 */
/**
 * Reconciles an agency's subscription state against the payment provider via
 * `POST /agencies/{agencyId}/subscription/reconcile`.
 *
 * @param agencyId - The identifier of the agency to reconcile
 * @returns A cancelable promise resolving to the refreshed agency detail
 */
export function reconcileSubscription(
agencyId: string,
): CancelablePromise<AgencyModelReturnFindById> {
    return __request(OpenAPIHousien, {
        method: 'POST',
        url: '/agencies/{agencyId}/subscription/reconcile',
        path: {
            'agencyId': agencyId,
        },
    });
}
